(() => {
  if (customElements.get('buyer-review-gallery')) return;

  class BuyerReviewGallery extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;

      this.dialog = this.querySelector('.buyer-reviews__dialog');
      if (!this.dialog) return;

      this.stage = this.querySelector('.buyer-reviews__stage-media');
      this.reviewer = this.querySelector('.buyer-reviews__reviewer');
      this.date = this.querySelector('.buyer-reviews__date');
      this.rating = this.querySelector('.buyer-reviews__rating');
      this.verified = this.querySelector('.buyer-reviews__verified');
      this.thumbnails = this.querySelector('.buyer-reviews__thumbnails');
      this.content = this.querySelector('.buyer-reviews__content');
      this.product = this.querySelector('.buyer-reviews__product');
      this.track = this.querySelector('.buyer-reviews__wall');
      this.carouselScrollbar = this.querySelector('.buyer-reviews-showcase__scrollbar');
      this.carouselScrollbarThumb = this.querySelector('.buyer-reviews-showcase__scrollbar-thumb');
      this.carouselPreviousButton = this.querySelector('.buyer-reviews-showcase__arrow--previous');
      this.carouselNextButton = this.querySelector('.buyer-reviews-showcase__arrow--next');
      this.previousButton = this.querySelector('.buyer-reviews__arrow--previous');
      this.nextButton = this.querySelector('.buyer-reviews__arrow--next');
      this.closeButton = this.querySelector('.buyer-reviews__close');
      this.records = this.readRecords();
      this.totalMedia = this.records.reduce((total, review) => total + review.media.length, 0);
      this.activeReviewIndex = 0;
      this.activeMediaIndex = 0;
      this.lastTrigger = null;
      this.suppressTileClickUntil = 0;

      this.querySelectorAll('.buyer-reviews__tile').forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
          if (performance.now() < this.suppressTileClickUntil) {
            event.preventDefault();
            return;
          }
          this.lastTrigger = trigger;
          this.openReview(Number(trigger.dataset.reviewIndex), 0);
        }, { signal: this.signal });
      });

      this.setupCarousel();

      this.closeButton.addEventListener('click', () => this.closeDialog(), { signal: this.signal });
      this.previousButton.addEventListener('click', () => this.move(-1), { signal: this.signal });
      this.nextButton.addEventListener('click', () => this.move(1), { signal: this.signal });
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.closeDialog();
      }, { signal: this.signal });
      this.dialog.addEventListener('keydown', (event) => this.handleKeydown(event), { signal: this.signal });
      this.dialog.addEventListener('close', () => this.handleClosed(), { signal: this.signal });
    }

    disconnectedCallback() {
      this.pauseVideo();
      document.body.classList.remove('buyer-reviews-dialog-open');
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.abortController) this.abortController.abort();
      this.initialized = false;
    }

    setupCarousel() {
      if (!this.track || !this.classList.contains('buyer-reviews--showcase')) return;

      this.carouselPreviousButton?.addEventListener('click', () => this.moveCarousel(-1), { signal: this.signal });
      this.carouselNextButton?.addEventListener('click', () => this.moveCarousel(1), { signal: this.signal });
      this.track.addEventListener('scroll', () => this.updateCarouselControls(), { signal: this.signal, passive: true });

      if (this.dataset.displayMode === 'scroll-snap') {
        this.setupMouseDrag();
        this.setupCarouselScrollbar();
      }

      if (typeof ResizeObserver === 'function') {
        this.resizeObserver = new ResizeObserver(() => this.updateCarouselControls());
        this.resizeObserver.observe(this.track);
      }

      requestAnimationFrame(() => this.updateCarouselControls());
    }

    setupCarouselScrollbar() {
      if (!this.carouselScrollbar || !this.carouselScrollbarThumb) return;

      const finishDrag = (event) => {
        if (!this.scrollbarDragState || event.pointerId !== this.scrollbarDragState.pointerId) return;
        if (this.carouselScrollbarThumb.hasPointerCapture(event.pointerId)) {
          this.carouselScrollbarThumb.releasePointerCapture(event.pointerId);
        }
        this.carouselScrollbar.classList.remove('is-dragging');
        this.scrollbarDragState = null;
      };

      this.carouselScrollbar.addEventListener('pointerdown', (event) => {
        if (event.target === this.carouselScrollbarThumb) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        event.preventDefault();
        this.scrollToScrollbarPointer(event.clientX);
      }, { signal: this.signal });

      this.carouselScrollbarThumb.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();
        this.scrollbarDragState = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startScrollLeft: this.track.scrollLeft
        };
        this.carouselScrollbarThumb.setPointerCapture(event.pointerId);
        this.carouselScrollbar.classList.add('is-dragging');
      }, { signal: this.signal });

      this.carouselScrollbarThumb.addEventListener('pointermove', (event) => {
        if (!this.scrollbarDragState || event.pointerId !== this.scrollbarDragState.pointerId) return;
        const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        const maxThumbTravel = Math.max(0, this.carouselScrollbar.clientWidth - this.carouselScrollbarThumb.offsetWidth);
        if (maxScroll <= 0 || maxThumbTravel <= 0) return;
        const distance = event.clientX - this.scrollbarDragState.startX;
        this.track.scrollLeft = this.scrollbarDragState.startScrollLeft + (distance * maxScroll / maxThumbTravel);
      }, { signal: this.signal });

      this.carouselScrollbarThumb.addEventListener('pointerup', finishDrag, { signal: this.signal });
      this.carouselScrollbarThumb.addEventListener('pointercancel', finishDrag, { signal: this.signal });
    }

    scrollToScrollbarPointer(clientX) {
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const maxThumbTravel = Math.max(0, this.carouselScrollbar.clientWidth - this.carouselScrollbarThumb.offsetWidth);
      if (maxScroll <= 0 || maxThumbTravel <= 0) return;
      const bounds = this.carouselScrollbar.getBoundingClientRect();
      const thumbOffset = Math.min(maxThumbTravel, Math.max(0, clientX - bounds.left - (this.carouselScrollbarThumb.offsetWidth / 2)));
      this.track.scrollTo({ left: thumbOffset / maxThumbTravel * maxScroll, behavior: 'smooth' });
    }

    setupMouseDrag() {
      let dragState = null;

      const finishDrag = (event) => {
        if (!dragState || event.pointerId !== dragState.pointerId) return;
        if (dragState.moved) this.suppressTileClickUntil = performance.now() + 300;
        if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
        this.track.classList.remove('is-dragging');
        dragState = null;
        this.updateCarouselControls();
      };

      this.track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        dragState = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startScrollLeft: this.track.scrollLeft,
          moved: false
        };
      }, { signal: this.signal });

      this.track.addEventListener('pointermove', (event) => {
        if (!dragState || event.pointerId !== dragState.pointerId) return;
        const distance = event.clientX - dragState.startX;
        if (Math.abs(distance) > 4 && !dragState.moved) {
          dragState.moved = true;
          this.track.setPointerCapture(event.pointerId);
          this.track.classList.add('is-dragging');
        }
        if (!dragState.moved) return;
        event.preventDefault();
        this.track.scrollLeft = dragState.startScrollLeft - distance;
      }, { signal: this.signal });

      this.track.addEventListener('pointerup', finishDrag, { signal: this.signal });
      this.track.addEventListener('pointercancel', finishDrag, { signal: this.signal });
    }

    moveCarousel(direction) {
      const firstTile = this.track?.querySelector('.buyer-reviews__tile');
      if (!firstTile) return;
      const gap = Number.parseFloat(getComputedStyle(this.track).columnGap) || 0;
      this.track.scrollBy({
        left: direction * (firstTile.getBoundingClientRect().width + gap),
        behavior: 'smooth'
      });
    }

    updateCarouselControls() {
      if (!this.track) return;
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      if (this.carouselPreviousButton) this.carouselPreviousButton.disabled = this.track.scrollLeft <= 1;
      if (this.carouselNextButton) this.carouselNextButton.disabled = this.track.scrollLeft >= maxScroll - 1;
      this.updateCarouselScrollbar(maxScroll);
    }

    updateCarouselScrollbar(maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth)) {
      if (!this.carouselScrollbar || !this.carouselScrollbarThumb) return;
      const scrollbarWidth = this.carouselScrollbar.clientWidth;
      const hasOverflow = maxScroll > 1 && scrollbarWidth > 0;
      this.carouselScrollbar.toggleAttribute('hidden', !hasOverflow);
      if (!hasOverflow) return;

      const visibleRatio = Math.min(1, this.track.clientWidth / this.track.scrollWidth);
      const thumbWidth = Math.max(48, scrollbarWidth * visibleRatio);
      const maxThumbTravel = Math.max(0, scrollbarWidth - thumbWidth);
      const thumbOffset = maxScroll > 0 ? Math.min(maxThumbTravel, Math.max(0, this.track.scrollLeft / maxScroll * maxThumbTravel)) : 0;
      this.carouselScrollbarThumb.style.width = `${thumbWidth}px`;
      this.carouselScrollbarThumb.style.transform = `translate3d(${thumbOffset}px, 0, 0)`;
    }

    readRecords() {
      return [...this.querySelectorAll('.buyer-reviews__record')].map((record) => ({
        name: record.querySelector('.buyer-reviews__record-name')?.textContent.trim() || '',
        date: record.querySelector('.buyer-reviews__record-date')?.textContent.trim() || '',
        rating: Math.min(5, Math.max(0, Number(record.dataset.rating) || 0)),
        verified: record.dataset.verified === 'true',
        content: record.querySelector('.buyer-reviews__record-content')?.innerHTML || '',
        product: record.querySelector('.buyer-reviews__record-product')?.innerHTML || '',
        media: [...record.querySelectorAll('.buyer-reviews__media-data')].map((item) => ({
          type: item.dataset.mediaType,
          preview: item.dataset.preview || '',
          template: item.querySelector('template')
        }))
      })).filter((review) => review.media.length > 0);
    }

    openReview(reviewIndex, mediaIndex) {
      if (!this.records[reviewIndex]) return;
      this.activeReviewIndex = reviewIndex;
      this.activeMediaIndex = mediaIndex;
      this.render();

      if (!this.dialog.open) {
        if (typeof this.dialog.showModal === 'function') {
          this.dialog.showModal();
        } else {
          this.dialog.setAttribute('open', '');
          this.dialog.setAttribute('role', 'dialog');
          this.dialog.setAttribute('aria-modal', 'true');
        }
      }

      document.body.classList.add('buyer-reviews-dialog-open');
      this.playVideo();
      requestAnimationFrame(() => this.closeButton.focus());
    }

    closeDialog() {
      if (typeof this.dialog.close === 'function') {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this.handleClosed();
      }
    }

    handleClosed() {
      this.pauseVideo();
      document.body.classList.remove('buyer-reviews-dialog-open');
      if (this.lastTrigger?.isConnected) this.lastTrigger.focus();
    }

    handleKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.move(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.move(1);
      } else if (event.key === 'Escape' && typeof this.dialog.close !== 'function') {
        event.preventDefault();
        this.closeDialog();
      }
    }

    move(direction) {
      if (this.totalMedia < 2) return;

      let nextReviewIndex = this.activeReviewIndex;
      let nextMediaIndex = this.activeMediaIndex + direction;

      if (nextMediaIndex >= this.records[nextReviewIndex].media.length) {
        nextReviewIndex = (nextReviewIndex + 1) % this.records.length;
        nextMediaIndex = 0;
      } else if (nextMediaIndex < 0) {
        nextReviewIndex = (nextReviewIndex - 1 + this.records.length) % this.records.length;
        nextMediaIndex = this.records[nextReviewIndex].media.length - 1;
      }

      this.activeReviewIndex = nextReviewIndex;
      this.activeMediaIndex = nextMediaIndex;
      this.render();
    }

    render() {
      const review = this.records[this.activeReviewIndex];
      if (!review) return;
      const media = review.media[this.activeMediaIndex];
      if (!media) return;

      this.pauseVideo();
      this.stage.replaceChildren(media.template.content.cloneNode(true));
      this.playVideo();
      this.reviewer.textContent = review.name;
      this.date.textContent = review.date;
      this.date.toggleAttribute('hidden', !review.date);
      this.content.innerHTML = review.content;
      if (this.product) {
        this.product.innerHTML = review.product;
        this.product.toggleAttribute('hidden', !review.product.trim());
      }
      this.renderRating(review.rating);

      this.verified.toggleAttribute('hidden', !review.verified);
      this.verified.querySelector('span').textContent = this.dataset.verifiedLabel || 'Verified purchase';
      this.renderThumbnails(review);

      const showArrows = this.totalMedia > 1;
      this.previousButton.toggleAttribute('hidden', !showArrows);
      this.nextButton.toggleAttribute('hidden', !showArrows);
    }

    renderRating(value) {
      this.rating.setAttribute('aria-label', `${value.toFixed(1)} out of 5 stars`);
      this.rating.querySelectorAll('.buyer-reviews__star').forEach((star, index) => {
        const percentage = Math.min(100, Math.max(0, (value - index) * 100));
        star.querySelector('.buyer-reviews__star-fill').style.width = `${percentage}%`;
      });
    }

    renderThumbnails(review) {
      const fragment = document.createDocumentFragment();

      review.media.forEach((media, mediaIndex) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'buyer-reviews__thumbnail';
        button.setAttribute('aria-label', `Show review media ${mediaIndex + 1}`);
        button.setAttribute('aria-current', String(mediaIndex === this.activeMediaIndex));

        if (media.preview) {
          const image = document.createElement('img');
          image.src = media.preview;
          image.alt = '';
          image.loading = 'lazy';
          button.append(image);
        }

        if (media.type === 'video') {
          const play = document.createElement('span');
          play.className = 'buyer-reviews__thumb-play';
          play.setAttribute('aria-hidden', 'true');
          button.append(play);
        }

        button.addEventListener('click', () => {
          this.activeMediaIndex = mediaIndex;
          this.render();
        }, { signal: this.signal });
        fragment.append(button);
      });

      this.thumbnails.replaceChildren(fragment);
      this.thumbnails.toggleAttribute('hidden', review.media.length < 2);
    }

    pauseVideo() {
      const video = this.stage?.querySelector('video');
      if (video) video.pause();
    }

    playVideo() {
      const video = this.stage?.querySelector('video');
      if (!video) return;
      video.autoplay = true;
      const playPromise = video.play();
      if (playPromise) playPromise.catch(() => {});
    }
  }

  customElements.define('buyer-review-gallery', BuyerReviewGallery);
})();
